import type { LegacyRDMDOpts as Opts, LegacyRDMDProps as Props } from '@readme/iso';

import { setup, react, reactTOC, reactProcessor, utils } from '@readme/markdown-legacy';
import React, { useContext, useMemo } from 'react';

// eslint-disable-next-line no-restricted-imports
import { BaseUrlContext, GlossaryContext, VariablesContext } from '@core/context';
import components from '@core/utils/rdmdComponentOverrides';

import './style.scss';

const truthy = (value: string | null) => {
  if (!value) return false;
  if (['on', 'true'].includes(value)) return true;
  if (['off', 'false'].includes(value)) return false;
  return !!value;
};

function useRDMD(bodyParam: string, toc = true, excerpt = false, optsParam: Opts) {
  const memoizedOpts = useMemo(() => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : {});

    return {
      ...optsParam,
      ...(params.has('rdmdCompatibilityMode') && { compatibilityMode: truthy(params.get('rdmdCompatibilityMode')) }),
      reusableContent: { ...optsParam.reusableContent, tags: optsParam.reusableContent?.tags || {}, wrap: false },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(optsParam)]);

  // normalize RDMD text and options
  const [body, opts] = useMemo(() => setup(bodyParam || '', memoizedOpts), [bodyParam, memoizedOpts]);
  const ast = useMemo(() => reactProcessor(opts, components).parse(body), [body, opts]);
  const content = useMemo(
    () => react(excerpt ? { type: 'root', children: ast.children.slice(0, 1) } : ast, opts, components),
    [ast, excerpt, opts],
  );
  const tocContent = useMemo(() => reactTOC(ast, opts), [ast, opts]);

  return toc ? tocContent : content;
}

export const TocContent = ({ body, opts = {} }) => {
  return useRDMD(body, true, false, opts);
};

const RdmdContent = ({
  body,
  children,
  skipBaseClassName,
  className,
  excerpt,
  opts = {},
  Tag = 'div',
  ...props
}: Props) => {
  const content = useRDMD((body || children || '') as string, false, excerpt, opts);
  const classes = [skipBaseClassName !== true && 'markdown-body', className || ''];

  return (
    <Tag {...props} className={`rm-Markdown ${classes.filter(c => c).join(' ')}`} data-testid="RDMD">
      {content}
    </Tag>
  );
};

/**
 * Some functions in `@readme/markdown-legacy` rely on these internal contexts being populated,
 * specfically when SSRing. We don't want to use the legacy contexts at the top level of the
 * app to prevent importing the legacy `@readme/markdown-legacy` package for app mount, so we need
 * to bind values to the internal contexts to the `RDMD` component when it is used.
 */
const {
  BaseUrlContext: RdmdBaseUrlContext,
  GlossaryContext: RdmdGlossaryContext,
  VariablesContext: RdmdVariablesContext,
} = utils;

export const TOC = props => {
  const variables = useContext(VariablesContext);
  const glossary = useContext(GlossaryContext);
  const baseUrl = useContext(BaseUrlContext);

  return (
    <RdmdBaseUrlContext.Provider value={baseUrl}>
      <RdmdGlossaryContext.Provider value={glossary}>
        <RdmdVariablesContext.Provider value={variables}>
          <TocContent {...props} />
        </RdmdVariablesContext.Provider>
      </RdmdGlossaryContext.Provider>
    </RdmdBaseUrlContext.Provider>
  );
};

const RDMD = (props: Props) => {
  const variables = useContext(VariablesContext);
  const glossary = useContext(GlossaryContext);
  const baseUrl = useContext(BaseUrlContext);

  return (
    <RdmdBaseUrlContext.Provider value={baseUrl}>
      <RdmdGlossaryContext.Provider value={glossary}>
        <RdmdVariablesContext.Provider value={variables}>
          <RdmdContent {...props} />
        </RdmdVariablesContext.Provider>
      </RdmdGlossaryContext.Provider>
    </RdmdBaseUrlContext.Provider>
  );
};

export default RDMD;
